//codex-- Keep the toolbar redesign scoped to pages in this folder.
import PageToolbar, { ToolbarField } from './PageToolbar';
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import HeadingCp   from '../components/HeadingCp';
import Modal       from '../components/Modal';
import Loading     from '../components/LoadingSpinner';
import { TrashIcon } from '@heroicons/react/24/outline';

const EquipmentList = () => {
  /* ──────────────────── state ──────────────────── */
  const [rows,   setRows]      = useState([]);           
  const [search, setSearch]    = useState('');
  const [editId, setEditId]    = useState(null);
  const [editRow,setEditRow]   = useState(null);
  const [open,   setOpen]      = useState(false);        
  const [addRow, setAddRow]    = useState({
    filter:'', device_name:'', full_description:'',
    sub_category:'', manufacture:'', device_price:'',
    isActive: 1, barcode_number: ''
  });

  const apiUrl   = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [hasAccess, setHasAccess] = useState(false);   // NEW
  const ADMIN = hasAccess;                             // CHANGED

  // ─────────────────────────── toolbar controls ───────────────────────────
  const [percent, setPercent]           = useState('');
  const [selectedMfgr, setSelectedMfgr] = useState('');
  const manufacturers = useMemo(
    () => [...new Set(rows.map(r => r.manufacture).filter(Boolean))],
    [rows]
  );

  /* ──────────────────── fetch settings + list ──────────────────── */
  useEffect(() => {
    (async () => {
      try {
        // 1) Get inventory settings / access flag
        const settingsRes = await axios.get(`${apiUrl}/inventory_crud.php`, {
          params: { getInventorySettings: true },
        });
        if (Array.isArray(settingsRes.data) && settingsRes.data[0] === 'success') {
          const s = settingsRes.data[1] || {};
          setHasAccess(!!s.access);
        } else if (settingsRes.data?.success && settingsRes.data.data) {
          const s = settingsRes.data.data;
          setHasAccess(!!s.access);
        } else {
          // if backend returns something unexpected, default to true or false as you prefer
          setHasAccess(true);
        }

        // 2) Load equipment list
        const { data } = await axios.get(`${apiUrl}/es_builder_crud.php`, {
          params : { equipmentBuilderList : 1 }
        });
        if (data[0] === 'success') setRows(data[1] || []);
        else toast.error('Could not load equipment list');
      } catch (err) {
        if (err.response?.status === 401) navigate('/login');
        else toast.error('Server error while loading equipment');
      }
    })();
  }, [apiUrl, navigate]);

  /* ──────────────────── helpers ──────────────────── */
  const q = search.toLowerCase();

  const filtered = useMemo(
    () => rows.filter(r =>
         (r.device_name       && r.device_name.toLowerCase().includes(q)) ||
         (r.full_description  && r.full_description.toLowerCase().includes(q)) ||
         (r.sub_category      && r.sub_category.toLowerCase().includes(q)) ||
         (r.manufacture       && r.manufacture.toLowerCase().includes(q)) ||
         (r.barcode_number    && r.barcode_number.toLowerCase().includes(q)) ||
         (r.filter            && r.filter.toLowerCase().includes(q))
       ),
    [rows, q]
  );

  const requireAccess = () => {
    if (!ADMIN) {
      toast.error("You don't have permission to modify the product list.", {
        position: 'top-center',
      });
      return false;
    }
    return true;
  };

  const updateBackend = async (payload, successMsg) => {
    try {
      const { data } = await axios.post(`${apiUrl}/es_builder_crud.php`, payload,
                                        { headers:{'Content-Type':'application/json'} });
      if (data.success) toast.success(successMsg);
      else toast.error(data?.error || 'Operation failed');
      return data;
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else toast.error('Server error');
      throw err;
    }
  };

  /* ──────────────────── CRUD handlers ──────────────────── */
  const onDelete = async id => {
    if (!requireAccess()) return;
    await updateBackend({ status:'deleteEquipmentBuilder', id },'Deleted');
    setRows(r => r.filter(x => x.id !== id));
  };

  const onSave = async () => {
    if (!requireAccess()) return;
    await updateBackend({ status:'equipmentBuilderSave', data:editRow },'Updated');
    setRows(r => r.map(x => (x.id === editRow.id ? editRow : x)));
    setEditId(null);
  };

  const onAdd = async () => {
    if (!requireAccess()) return;
    const res = await updateBackend(
      { status:'equipmentBuilderSave', data:addRow },
      'Added'
    );

    if (res?.success && res?.id) {
      setRows(r => [...r, { id: res.id, ...addRow }]);
    }

    setAddRow({ filter:'', device_name:'', full_description:'', sub_category:'', device_price:'', manufacture:'', barcode_number:''});
    setOpen(false);
  };

  const toggleActive = async (row) => {
    if (!requireAccess()) return;
    const newVal = row.isActive ? 0 : 1;
    const res = await updateBackend(
      { status: 'equipmentBuilderSave', data: { ...row, isActive: newVal } },
      newVal ? 'Activated' : 'De-activated'
    );
    if (res?.success) {
      setRows(rs => rs.map(x => x.id === row.id ? { ...x, isActive: newVal } : x));
    }
  };

  const adjustPrices = async (direction) => {
    if (!requireAccess()) return;

    const pct   = parseFloat(percent);
    if (!pct || !selectedMfgr) return;

    const res = await updateBackend(
      {
        status : 'bulkPriceAdjust',
        manufacture : selectedMfgr,
        percent : pct,
        direction
      },
      `${direction === 'inc' ? 'Increased' : 'Decreased'} ${pct}%`
    );
    if (res?.success) {
      const factor = 1 + (direction === 'inc' ? pct : -pct) / 100;
      setRows(rs => rs.map(r =>
        r.manufacture === selectedMfgr
          ? { ...r, device_price: (r.device_price * factor).toFixed(2) }
          : r
      ));
      setPercent('');
    }
  };

  /* ──────────────────── render ──────────────────── */
  return (
  <>
    <div className="mx-auto max-w-[1250px] px-4 py-8">
      {/* toolbar */}
      {/* ─── BULK PRICE ADJUSTER ─── */}
      <>{/*codex-- Match the updated toolbar: grouped actions, labeled controls, and lighter original colors. */}
        <PageToolbar title="Equipment tools" actions={<>
          <button onClick={() => ADMIN ? setOpen(true) : requireAccess()} className="bg-indigo-600 text-white px-4 py-3 rounded-md text-sm shadow-sm hover:bg-indigo-500">Add Equipment</button>
        </>}>
          <ToolbarField label="Manufacturer"><select className="border rounded-md p-2 text-sm" value={selectedMfgr} onChange={e => setSelectedMfgr(e.target.value)}>
              <option value="">Manufacturer…</option>
              {manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
            </select></ToolbarField>
          <ToolbarField label="Price adjustment (%)"><input type="number" step="0.01" className="w-24 border rounded-md p-2 text-sm" placeholder="%" value={percent} onChange={e => setPercent(e.target.value)} /></ToolbarField>
          <button onClick={() => adjustPrices('inc')} disabled={!percent || !selectedMfgr} className="bg-emerald-600 text-white px-3 py-2 rounded-md text-sm shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">Increase %</button>
          <button onClick={() => adjustPrices('dec')} disabled={!percent || !selectedMfgr} className="bg-rose-600 text-white px-3 py-2 rounded-md text-sm shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">Decrease %</button>
          <ToolbarField label="Search"><input className="flex-grow border rounded-md p-2" placeholder="Search …" value={search} onChange={e => setSearch(e.target.value)} /></ToolbarField>
        </PageToolbar>
      </>

      {rows.length===0 ? <Loading/> : (
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-200 text-sm font-semibold">
            <tr>
              {['Active | ID','Filter','Device | Description','Sub-Cat','Manufacture','Price', 'Barcode', ''].map(h=>(
                <th key={h} className="px-3 py-2 text-left border">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((r,i)=>(
              <tr key={r.id} className={i%2? 'bg-gray-100':'bg-white'}>
                {editId===r.id ? (
                  <>
                    <td className="px-3 py-2">
                      <TrashIcon
                        className="inline h-5 text-red-600 cursor-pointer"
                        onClick={()=>onDelete(r.id)}
                      />
                    </td>
                    <td className="px-3 py-1">
                      <input name="filter"
                          value={editRow.filter}
                          required
                          onChange={e=>setEditRow({...editRow,filter:e.target.value})}
                          className="w-full border rounded"/>
                    </td>

                    <td className="px-3 py-1">
                      <input name="device_name"
                          value={editRow.device_name}
                          required
                          onChange={e=>setEditRow({...editRow,device_name:e.target.value})}
                          className="w-full border rounded"/>
                      
                      <textarea name="full_description" rows={2}
                          value={editRow.full_description}
                          required
                          onChange={e=>setEditRow({...editRow,full_description:e.target.value})}
                          className="w-full border rounded"/>                        
                    </td>

                    <td className="px-3 py-1">
                      <input name="sub_category"
                          value={editRow.sub_category}
                          required
                          onChange={e=>setEditRow({...editRow,sub_category:e.target.value})}
                          className="w-full border rounded"/>
                    </td>

                    <td className="px-3 py-1">
                      <input name="manufacture"
                          value={editRow.manufacture}
                          required
                          onChange={e=>setEditRow({...editRow,manufacture:e.target.value})}
                          className="w-full border rounded"/>
                    </td>

                    <td className="px-3 py-1">
                      <input name="device_price" type="number" step="0.01"
                          value={editRow.device_price}
                          required
                          onChange={e=>setEditRow({...editRow,device_price:e.target.value})}
                          className="w-full border rounded"/>
                    </td>
                    <td className="px-3 py-1">
                      <input name="barcode_number"
                          value={editRow.barcode_number}
                          onChange={e=>setEditRow({...editRow,barcode_number:e.target.value})}
                          className="w-full border rounded"/>
                    </td>                  

                    <td className="px-3 py-2 text-right">
                      <button onClick={onSave} className="text-indigo-600 pr-3">
                        Save
                      </button>
                      <button onClick={() => setEditId(null)} className="block w-full text-gray-500 hover:underline">
                        Cancel
                      </button>                      
                    </td>

                  </>
                ):(
                  <>
                    <td className="px-3 py-2">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!r.isActive}
                          onChange={() => toggleActive(r)}
                        />
                        <span>{r.id}</span>
                      </label>
                    </td>
                    <td className="px-3 py-2">{r.filter}</td>
                    <td className="px-3 py-2">
                      {r.device_name}
                      <div className='text-xs'>{r.full_description}</div>
                    </td>
                    <td className="px-3 py-2">{r.sub_category}</td>
                    <td className="px-3 py-2">{r.manufacture}</td>
                    <td className="px-3 py-2">${Number(r.device_price).toFixed(2)}</td>
                    <td className="px-3 py-2">{r.barcode_number}</td>
                    <td className="px-3 py-2 text-right">
                      {ADMIN && (
                        <button
                          onClick={()=>{setEditId(r.id);setEditRow({...r});}}
                          className="text-indigo-600 pr-3">Edit</button>                        
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>

    {/* ───────── add-modal ───────── */}
    <Modal isOpen={open} onClose={()=>setOpen(false)}>
      <h2 className="text-lg font-semibold mb-4">Add New Equipment</h2>
      <div className="space-y-3">
        {['filter','device_name','sub_category', 'manufacture', 'device_price'].map(f=>(
          <input key={f} name={f} required
            placeholder={f.replace('_',' ')}
            value={addRow[f]} onChange={e=>setAddRow({...addRow,[f]:e.target.value})}
            className="w-full border rounded p-2 text-sm"
          />
        ))}
        <textarea name="full_description" rows={3} placeholder="Full description"
          value={addRow.full_description}
          onChange={e=>setAddRow({...addRow,full_description:e.target.value})}
          className="w-full border rounded p-2 text-sm"/>
        <button
          disabled={!addRow.device_name || !addRow.sub_category}
          onClick={onAdd}
          className={`float-right mt-2 px-4 py-2 rounded text-white
                      ${addRow.device_name? 'bg-indigo-600 hover:bg-indigo-700':'bg-gray-400'}`}>
          Add
        </button>
      </div>
    </Modal>
  </>
  );
};

export default EquipmentList;
